import { Schema } from "effect"
import { AllowEntry } from "../allow"
import { AgentKind, ModelChoice, ModelProvider, Owned, RecordMeta } from "../common"
import {
  AgentId,
  BranchName,
  CredentialId,
  Handle,
  IntegrationId,
  Markdown,
  Millis,
  PolicyId,
  PosInt,
  ProjectId,
  RepoId,
  Sha256,
  Slug,
  UserId,
  WorkspaceId,
} from "../ids"
import { Requires } from "../placement"
import { RoutingPolicy } from "../routing"
import { AccessRequest } from "../runner"

// #region workspace
/** `viewer` reads and comments; it issues no plan, ledger, memory or run command (decision 0003 #7). */
export const MemberRole = Schema.Literals(["owner", "admin", "member", "viewer"])

/**
 * Decision 0129 (0126 §1): a person's role in one project. `admin` changes the project's settings and members (today's
 * "project manager": its creator plus people made admin); `member` does almost everything else (0126 §2). Workspace
 * owners and admins are admins of every project.
 */
export const ProjectRole = Schema.Literals(["admin", "member"])
export type ProjectRole = typeof ProjectRole.Type

/**
 * Domain view of a Better Auth `member` row plus profile. Better Auth (D1) is the source of truth. A project's roster
 * (decision 0129) holds its members only: the people with a `project_member` row plus the workspace owners and admins.
 */
export const Member = Schema.Struct({
  userId: UserId,
  /** The workspace role. */
  role: MemberRole,
  displayName: Schema.String,
  handle: Handle, // @jannik
  avatarUrl: Schema.NullOr(Schema.String),
  /**
   * Decision 0129: the role in this project (D1 `project_member`, workspace owners and admins always `admin`). Absent on
   * rosters synced before 0129: the project's owner and workspace owners/admins are admins, everyone else a member.
   */
  projectRole: Schema.optionalKey(ProjectRole),
})

/** A repository known to the workspace. Runners register local clones of it (`minga repo add`); paths never leave the machine. */
export const Repo = Schema.Struct({
  id: RepoId,
  ...Owned,
  remote: Schema.String, // normalised: "github.com/<org>/dictly"
  defaultBranch: Schema.String,
  createdAt: Millis,
})

/** Metadata only. Secret material is encrypted at rest in the owning DO and decrypted only by the Agent DO
 * (platform mind) or the egress proxy (sandboxes). It never appears in an event, a snapshot or a job. */
export const ModelCredential = Schema.Struct({
  id: CredentialId,
  ...Owned,
  scope: Schema.TaggedUnion({ workspace: {}, user: { userId: UserId } }),
  provider: ModelProvider,
  kind: Schema.Literals(["api_key", "chatgpt_plan"]),
  label: Schema.String,
  hint: Schema.String, // e.g. "…a4f2"
  createdAt: Millis,
})

/** Workspace integration registry entry (Phase 2, tool gateway). Grants to areas live in the Project DO. */
export const Integration = Schema.Struct({
  id: IntegrationId,
  ...Owned,
  key: Slug, // "openseo", "appstoreconnect"
  transport: Schema.Literals(["http", "stdio"]),
  /** stdio servers run on a machine; `executor` routes their publish runs (e.g. asc-mcp needs the Mac). */
  executor: Schema.NullOr(Requires),
  tools: Schema.Array(
    Schema.Struct({
      name: Schema.String,
      class: Schema.Literals(["read", "write", "outward"]),
      /** Pinned description+schema hash. A change requires re-approval (rug-pull defence). */
      schemaHash: Sha256,
    }),
  ),
  createdAt: Millis,
})
// #endregion workspace

// #region project
export const Cadence = Schema.Struct({
  timezone: Schema.String, // IANA, e.g. "Europe/Berlin"
  digestCron: Schema.NullOr(Schema.String), // null = on request only
  weeklyReview: Schema.NullOr(Schema.Struct({ weekday: Schema.Int, hour: Schema.Int })),
  cycleWeeks: Schema.NullOr(Schema.Int),
  /**
   * Wave C (decision 0071): the daily loose-ends check's hour (0-23) in `timezone`. Absent = 9 (09:00), null = off.
   * Project managers change it (and `timezone`) through UpdateProject.patch.cadence.
   */
  dailyCheckHour: Schema.optionalKey(
    Schema.NullOr(Schema.Int.check(Schema.isBetween({ minimum: 0, maximum: 23 }))),
  ),
})
export type Cadence = typeof Cadence.Type

/**
 * How a temporary worker of this project works (settings wave S9, §5.5): the preferred CLI, model and effort per CLI,
 * whether it works in the project's repo, and a narrowing request. How it acts on a machine is the runner profile's.
 */
export const WorkerPreset = Schema.Struct({
  key: Slug, // "default" always exists
  label: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(40)),
  /** Preferred CLI; null = any. */
  agent: Schema.NullOr(AgentKind),
  /** Used when the worker lands on a Claude profile. */
  claude: ModelChoice,
  /** Used when the worker lands on a Codex profile. */
  codex: ModelChoice,
  /** Used when the worker lands on an Antigravity profile (decision 0109). Absent = no choice (CLI default). */
  antigravity: Schema.optionalKey(ModelChoice),
  /** Used when the worker lands on a Muse Code profile (decision 0110; absent = the CLI's defaults). */
  muse: Schema.optionalKey(ModelChoice),
  /** Narrowing only (`{mcp: "minga_only"}` = Minga's tools only). */
  access: AccessRequest,
  /** "none": no repo, no gh, a scratch directory and no PR (research, data). */
  repo: Schema.Literals(["project", "none"]),
})
export type WorkerPreset = typeof WorkerPreset.Type
export const WorkerSettings = Schema.Struct({
  presets: Schema.Array(WorkerPreset).check(Schema.isMaxLength(8)),
  /** Optional per-item budgets since a person last acted. Missing/null = no count-based human gate. */
  attemptCap: Schema.optionalKey(Schema.NullOr(PosInt)),
  noProgressCap: Schema.optionalKey(Schema.NullOr(PosInt)),
})
export type WorkerSettings = typeof WorkerSettings.Type

export const DEFAULT_WORKER_PRESET: WorkerPreset = {
  key: "default",
  label: "Default",
  agent: null,
  claude: { model: null, effort: null },
  codex: { model: null, effort: null },
  access: {},
  repo: "project",
}
export const NO_REPO_WORKER_PRESET: WorkerPreset = {
  key: "no-repo",
  label: "No repo: research, data",
  agent: null,
  claude: { model: null, effort: null },
  codex: { model: null, effort: null },
  access: {},
  repo: "none",
}
/** What a project without `workers` uses. */
export const DEFAULT_WORKER_SETTINGS: WorkerSettings = {
  presets: [DEFAULT_WORKER_PRESET, NO_REPO_WORKER_PRESET],
}

/** This project's base branch for one of its repos (absent = the registry's default branch). */
export const RepoBaseBranch = Schema.Struct({ repoId: RepoId, branch: BranchName })
export type RepoBaseBranch = typeof RepoBaseBranch.Type

/** Decision 0049: the brief (Project.description) is at most this long; it is in every pack. */
export const BRIEF_MAX_CHARS = 8_000

export const Project = Schema.Struct({
  ...RecordMeta,
  id: ProjectId,
  workspaceId: WorkspaceId,
  slug: Slug,
  name: Schema.String,
  /**
   * The brief (decision 0049): vision, for whom, principles, not doing, how to build and test. Written only by
   * CreateProject and ReviseBrief; old ProjectUpdated events still set it.
   */
  description: Markdown,
  status: Schema.Literals(["active", "paused", "archived"]),
  leadAgentId: Schema.NullOr(AgentId),
  repos: Schema.Array(RepoId),
  /** Planner rule: at most this many top-level plannable items in Now in the project (default 3). */
  wip: Schema.Struct({ maxNow: Schema.Int }),
  cadence: Cadence,
  /** Project default policy; areas and agents inherit from it. */
  policyId: PolicyId,
  /** Settings wave: worker presets; absent = DEFAULT_WORKER_SETTINGS. */
  workers: Schema.optionalKey(WorkerSettings),
  routing: Schema.optionalKey(RoutingPolicy),
  /** Settings wave: per-repo base branches; a repo without an entry uses the registry's default branch. */
  baseBranches: Schema.optionalKey(Schema.Array(RepoBaseBranch)),
  /**
   * Decision 0096: the project's sandbox allowlist, added to the workspace's for this project's runs. Project managers
   * edit it (UpdateProject.patch.allow); "Always allow" on a permission card adds to it.
   */
  allow: Schema.optionalKey(Schema.Array(AllowEntry)),
})
export type Project = typeof Project.Type
// #endregion project
